import { Difficulty } from "@ravalmatic/euchre-engine";
import { GameConstants } from "./GameConstants";

export type Language = "en" | "es";

export interface GameData {
    difficulty: Difficulty;
    musicMuted: boolean;
    sfxMuted: boolean;
    language: Language;
}

export class PersistentDataManager {
    public static gameData = PersistentDataManager.createDefaultGameData();
    public static notice = "";

    public static createDefaultGameData(): GameData {
        return { difficulty: Difficulty.EASY, musicMuted: false, sfxMuted: false, language: "en" };
    }

    public static readGameData(): void {
        this.gameData = this.createDefaultGameData();
        this.notice = "";
        try {
            const stored = localStorage.getItem(GameConstants.SAVED_GAME_DATA_KEY);
            if (stored !== null) {
                const data: unknown = JSON.parse(stored);
                if (!this.isGameData(data)) throw new Error("Invalid saved game data");
                this.gameData = data;
                return;
            }
            const difficulty = localStorage.getItem("euchre-difficulty");
            if (difficulty !== null) {
                if (!Object.values(Difficulty).includes(difficulty as Difficulty)) {
                    throw new Error("Unrecognized saved difficulty");
                }
                this.gameData.difficulty = difficulty as Difficulty;
            }
            for (const [field, key] of [
                ["musicMuted", "euchre-music-muted"], ["sfxMuted", "euchre-sfx-muted"]
            ] as const) {
                const value = localStorage.getItem(key);
                if (value !== null && value !== "true" && value !== "false") {
                    throw new Error(`Invalid saved audio setting: ${key}`);
                }
                this.gameData[field] = value === "true";
            }
            this.writeGameData();
        } catch (error) {
            console.warn("Could not load Euchre preferences", error);
            this.gameData = this.createDefaultGameData();
            this.notice = "Settings could not be loaded. Using defaults.";
        }
    }

    public static updateGameData<K extends keyof GameData>(key: K, value: GameData[K]): void {
        this.gameData[key] = value;
        this.writeGameData();
    }

    public static writeGameData(): void {
        try {
            localStorage.setItem(GameConstants.SAVED_GAME_DATA_KEY, JSON.stringify(this.gameData));
            this.notice = "";
        } catch (error) {
            console.warn("Could not save Euchre preferences", error);
            this.notice = "Settings could not be saved on this device.";
        }
    }

    private static isGameData(value: unknown): value is GameData {
        return typeof value === "object" && value !== null
            && "difficulty" in value && Object.values(Difficulty).includes(value.difficulty as Difficulty)
            && "musicMuted" in value && typeof value.musicMuted === "boolean"
            && "sfxMuted" in value && typeof value.sfxMuted === "boolean"
            && "language" in value && (value.language === "en" || value.language === "es");
    }
}
